"use client";

import { GoTrash } from "react-icons/go";
import { useTranslation } from "react-i18next";

type Client = {
  name: string;
  phone: string;
  email?: string;
  exists?: boolean;
  errors?: {
    name?: boolean;
    phone?: boolean;
    email?: boolean;
  } | null;
};

export default function ClientImportTable({
  clients,
  deleteRow,
}: {
  clients: Client[];
  deleteRow: (index: number) => void;
}) {
  const { t } = useTranslation();

  if (!clients || clients.length === 0) {
    return (
      <div className="w-full text-center text-lg">
        {t("previewTableHeaders.noClients") || "No clients available."}
      </div>
    );
  }

  // Check if any client has at least one error
  const hasErrors = clients.some(
    (client) => client.errors && Object.values(client.errors).some(Boolean)
  );

  return (
    <div className="thin-scrollbar overflow-x-auto py-1">
      <table className="min-w-full border-collapse border border-gray-300 text-center">
        <thead>
          <tr className="bg-gray-100">
            <th className="border border-gray-300 p-2">
              {t("previewTableHeaders.number") || "#"}
            </th>
            <th className="border border-gray-300 p-2">
              {t("previewTableHeaders.customerName") || "Customer Name"}
            </th>
            <th className="border border-gray-300 p-2">
              {t("previewTableHeaders.phoneNumber") || "Phone Number"}
            </th>
            <th className="border border-gray-300 p-2">
              {t("previewTableHeaders.email") || "Email"}
            </th>
            {hasErrors && (
              <th className="border border-gray-300 p-2">
                {t("previewTableHeaders.actions") || "Actions"}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {clients.map((client, index) => {
            const rowHasError =
              client.errors && Object.values(client.errors).some(Boolean);

            return (
              <tr
                key={index}
                className={`hover:bg-gray-50 ${
                  client.exists ? "border-2 border-blue-400" : ""
                }`}
              >
                <td className="border border-gray-300 p-2">{index + 1}</td>

                <td
                  className={`p-2 ${
                    client?.errors?.name
                      ? "border-2 border-red-400 font-semibold text-red-500"
                      : "border border-gray-300"
                  }`}
                >
                  {client.name || "-"}
                </td>

                <td
                  className={`p-2 ${
                    client?.errors?.phone
                      ? "border-2 border-red-400 font-semibold text-red-500"
                      : "border border-gray-300"
                  }`}
                >
                  {client.phone || "-"}
                </td>

                <td
                  className={`p-2 ${
                    client?.errors?.email
                      ? "border-2 border-red-400 font-semibold text-red-500"
                      : "border border-gray-300"
                  }`}
                >
                  {client.email || "-"}
                </td>

                {hasErrors && (
                  <td className="border border-gray-300 p-2">
                    {rowHasError && (
                      <button onClick={() => deleteRow(index)}>
                        <GoTrash
                          size={20}
                          className="text-red-400 hover:text-red-600"
                        />
                      </button>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
